import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { wellnessService } from '../../../services/wellnessService';

const ServiceSelector = ({ selectedServices = [], onServiceToggle }) => {
  const [activeCategory, setActiveCategory] = useState('rejuvenation');
  const [services, setServices] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // RE-ENABLED - Fetch services from database on component mount (SAFE - only runs once on mount)
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);
        const servicesData = await wellnessService.getWellnessServicesByCategory();
        setServices(servicesData);

        // Set first available category as active
        const categories = Object.keys(servicesData);
        if (categories.length > 0) {
          setActiveCategory(categories[0]);
        }
      } catch (err) {
        console.error('Error fetching wellness services:', err);
        setError('Failed to load services. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []); // Empty dependency array = runs only once on mount

  // Helper function to format duration
  const formatDuration = (durationMinutes) => {
    if (!durationMinutes || durationMinutes === 0) {
      return 'Multiple sessions';
    }
    return `${durationMinutes} min`;
  };

  // Helper function to format price
  const formatPrice = (basePrice) => {
    if (!basePrice || basePrice === 0) {
      return '₹—';
    }
    return `₹${basePrice}`;
  };

  // Transform database service to component format
  const transformService = (dbService) => {
    return {
      id: dbService.slug || dbService.id,
      dbId: dbService.id,
      name: dbService.name,
      description: dbService.description,
      duration: formatDuration(dbService.duration_minutes),
      duration_minutes: dbService.duration_minutes, // Keep for calculations
      price: formatPrice(dbService.base_price),
      base_price: dbService.base_price, // Keep for calculations
      image: dbService.image_url || '/assets/images/default-therapy.jpg',
      popular: dbService.is_popular || false,
      category: dbService.category,
      therapists_required: dbService.therapists_required || 1 // Important for availability check!
    };
  };

  const handleServiceClick = (service) => {
    const transformedService = transformService(service);
    const isSelected = selectedServices?.some(s => s?.id === transformedService?.id);

    let updatedServices;
    if (isSelected) {
      updatedServices = selectedServices?.filter(s => s?.id !== transformedService?.id);
    } else {
      updatedServices = [...selectedServices, transformedService];
    }

    onServiceToggle(updatedServices);
  };

  const isServiceSelected = (serviceId) => {
    return selectedServices?.some(s => s?.id === serviceId);
  };

  // Category display names
  const categoryNames = {
    'rejuvenation': 'Rejuvenation',
    'pain-relief': 'Pain Relief',
    'allergy-sinusitis': 'Allergy & Sinusitis',
    'anti-stress': 'Anti-Stress',
    'anti-obesity': 'Anti-Obesity',
    'muscle-building': 'Muscle Building',
    'post-natal': 'Post-Natal Care'
  };

  // Calculate totals
  const safeSelectedServices = selectedServices || [];
  const totalPrice = safeSelectedServices?.reduce((sum, service) => {
    if (!service?.base_price || typeof service?.base_price !== 'number') {
      return sum;
    }
    return sum + service?.base_price;
  }, 0);

  const totalDuration = safeSelectedServices?.reduce((sum, service) => {
    const duration = service?.duration_minutes || 0;
    return sum + duration;
  }, 0);

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
        <p className="text-gray-600">Loading services...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Icon name="AlertCircle" className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-600 text-center">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Empty state
  if (Object.keys(services).length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Icon name="Package" className="w-12 h-12 text-gray-400 mb-4" />
        <p className="text-gray-600">No services available at the moment.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Category Tabs */}
      <div className="flex overflow-x-auto gap-3 pb-2 scrollbar-hide">
        {Object.keys(services).map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-6 py-3 rounded-full font-semibold whitespace-nowrap transition-all ${activeCategory === category
              ? 'bg-purple-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            {categoryNames[category] || category}
          </button>
        ))}
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services[activeCategory]?.map((service) => {
          const isSelected = isServiceSelected(service.slug || service.id);

          return (
            <div
              key={service.id}
              onClick={() => handleServiceClick(service)}
              className={`relative cursor-pointer rounded-xl overflow-hidden transition-all duration-300 ${isSelected
                ? 'ring-4 ring-purple-600 shadow-2xl scale-105'
                : 'hover:shadow-xl hover:scale-102'
                }`}
            >
              {/* Popular Badge */}
              {service.is_popular && (
                <div className="absolute top-4 right-4 z-10 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                  <Icon name="Star" className="w-3 h-3" />
                  Popular
                </div>
              )}

              {/* Selection Indicator */}
              {isSelected && (
                <div className="absolute top-4 left-4 z-10 bg-purple-600 text-white p-2 rounded-full">
                  <Icon name="Check" className="w-4 h-4" />
                </div>
              )}

              {/* Service Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                  src={service.image_url || '/assets/images/default-therapy.jpg'}
                  alt={service.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              </div>

              {/* Service Details */}
              <div className="p-6 bg-white">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                  {service.description}
                </p>

                {/* Service Info */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2 text-gray-700">
                    <Icon name="Clock" className="w-4 h-4" />
                    <span className="text-sm">{formatDuration(service.duration_minutes)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Icon name="Users" className="w-4 h-4" />
                    <span className="text-sm">{service.therapists_required} therapist{service.therapists_required > 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Price */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <span className="text-2xl font-bold text-purple-600">
                    {formatPrice(service.base_price)}
                  </span>
                  <button
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${isSelected
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {isSelected ? 'Selected' : 'Select'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected Services Summary */}
      {safeSelectedServices.length > 0 && (
        <div className="mt-8 p-6 bg-purple-50 rounded-xl border-2 border-purple-200">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Selected Services ({safeSelectedServices.length})
          </h3>

          <div className="space-y-3 mb-4">
            {safeSelectedServices.map((service) => (
              <div
                key={service.id}
                className="flex items-center justify-between p-3 bg-white rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Icon name="Check" className="w-5 h-5 text-purple-600" />
                  <span className="font-medium text-gray-900">{service.name}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-gray-600">{service.duration}</span>
                  <span className="font-semibold text-purple-600">{service.price}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleServiceClick(service);
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Icon name="X" className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="flex items-center justify-between pt-4 border-t-2 border-purple-200">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Icon name="Clock" className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-gray-900">
                  Total: {totalDuration} min
                </span>
              </div>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              Total: ₹{totalPrice}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceSelector;